import { useEffect, useState } from 'react';

import { css } from '@compiled/react';

import { Pressable, xcss } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';

import { usePrefersReducedMotion } from './jsm-playground-motion';

export type JsmPlaygroundSkipAnimationPlacement = 'start' | 'end';

type JsmPlaygroundSkipAnimationButtonProps = {
	isVisible: boolean;
	label: string;
	onClick: () => void;
	placement: JsmPlaygroundSkipAnimationPlacement;
};

const SKIP_ANIMATION_FADE_MS = 300;

const buttonStyles = xcss({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	paddingBlock: 'space.100',
	paddingInline: 'space.150',
	backgroundColor: 'elevation.surface',
	borderStyle: 'none',
	boxShadow: 'elevation.shadow.overlay',
	color: 'color.text',
	font: 'font.body',
	fontWeight: 'font.weight.medium',
	whiteSpace: 'nowrap',
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
	},
	':active': {
		backgroundColor: 'elevation.surface.pressed',
	},
});

const skipAnimationStyles = css({
	position: 'absolute',
	insetBlockEnd: token('space.200'),
	zIndex: 800,
	display: 'flex',
	width: 'fit-content',
	opacity: 1,
	transitionProperty: 'opacity',
	transitionDuration: `${SKIP_ANIMATION_FADE_MS}ms`,
	transitionTimingFunction: 'ease-out',
	'@media (prefers-reduced-motion: reduce)': {
		transitionProperty: 'none',
	},
});

const startStyles = css({
	insetInlineStart: token('space.200'),
	insetInlineEnd: 'auto',
});

const endStyles = css({
	insetInlineStart: 'auto',
	insetInlineEnd: token('space.200'),
});

const fadingStyles = css({
	opacity: 0,
	pointerEvents: 'none',
});

export const JsmPlaygroundSkipAnimationButton = ({
	isVisible,
	label,
	onClick,
	placement,
}: JsmPlaygroundSkipAnimationButtonProps) => {
	const prefersReducedMotion = usePrefersReducedMotion();
	const [isRendered, setIsRendered] = useState(isVisible);
	const [isFadingOut, setIsFadingOut] = useState(false);

	useEffect(() => {
		if (isVisible) {
			setIsRendered(true);
			setIsFadingOut(false);
			return;
		}
		if (!isRendered) {
			return;
		}
		if (prefersReducedMotion) {
			setIsRendered(false);
			setIsFadingOut(false);
			return;
		}
		setIsFadingOut(true);
		const timeout = window.setTimeout(() => {
			setIsRendered(false);
		}, SKIP_ANIMATION_FADE_MS);
		return () => window.clearTimeout(timeout);
	}, [isRendered, isVisible, prefersReducedMotion]);

	if (!isRendered) {
		return null;
	}

	return (
		<div
			css={[
				skipAnimationStyles,
				placement === 'start' && startStyles,
				placement === 'end' && endStyles,
				isFadingOut && fadingStyles,
			]}
		>
			<Pressable
				xcss={buttonStyles}
				style={{ borderRadius: token('radius.full') }}
				onClick={onClick}
				aria-hidden={isFadingOut || undefined}
				// @ts-expect-error -- inert is valid HTML but is missing from React types
				inert={isFadingOut ? '' : undefined}
			>
				{label}
			</Pressable>
		</div>
	);
};
